import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { DialogType, ConfirmDialogConfig, MeConfirmComponent } from './me-confirm.component';
import { TranslateService } from '@ngx-translate/core';
import { BackPriority, NavigationService } from 'src/app/system/utility/navigation.service';
import { ResponsiveDesignService } from 'src/app/common/services/responsive-design/responsive-design.service';

@Injectable({
    providedIn: 'root'
})
export class MeConfirmDialogService {

    private dialogRef!: MatDialogRef<MeConfirmComponent, boolean | null>;

    constructor(private srvDialog: MatDialog, 
        private translate: TranslateService,
        private srvNav: NavigationService,
        private srvRpDesign: ResponsiveDesignService) { }

    private _isProcessing : boolean = false;

    private readonly setDialogConfig = (data: ConfirmDialogConfig, options?: { disableClose?: boolean }) => {
        const dialogConfig = new MatDialogConfig<ConfirmDialogConfig>();
        dialogConfig.data = data;
        dialogConfig.maxWidth = 600;
        dialogConfig.width = '98%';
        dialogConfig.height = 'auto';
        dialogConfig.panelClass = 'custom-dialog-confirm-messages';
        if (options?.disableClose) {
            dialogConfig.disableClose = true;
        }
        return dialogConfig;
    };

    public startProcessing() {
        this._isProcessing = true;
    }

    get isProcessing() : boolean {
        return this._isProcessing;
    }

    public closeDialogIfOpen(): void {
        if (this.dialogRef && this.dialogRef.getState() == MatDialogState.OPEN) {
            this.dialogRef.close();
        }
    }
    
    public showDialog(config: ConfirmDialogConfig, options?: { disableClose?: boolean }): Observable<boolean | null | undefined> {
        // Close the previous dialog if exist
        if ((this.dialogRef) && (this.dialogRef.getState() == MatDialogState.OPEN)) {
            this.dialogRef.close();
        }
        this.dialogRef = this.srvDialog.open(MeConfirmComponent, this.setDialogConfig(config, options));
        if (this.srvRpDesign.isMobileAndroid()) {
            const unregister = this.srvNav.registerBackHandler(
                BackPriority.DIALOG,
                () => {
                    if (!this.dialogRef || this.dialogRef.getState() !== MatDialogState.OPEN) {
                        return of(false);
                    }
                    this.dialogRef.close();
                    return of(true);
                },
                'dialog:me-confirm'
            );
            this.dialogRef.afterClosed().subscribe(() => unregister());
        }

        return this.dialogRef.afterClosed().pipe(tap((ret) => { this._isProcessing = false; return ret; }));
    }

    /**
     * Short Cut to show Simple Confirm Dialog
     */
    public showConfirmDialog(title: string, message: string, confirmButtonText: string = "BUTTON_CONFIRM", cancelButtonText: string = 'LABEL_NO'): Observable<boolean | null | undefined> {
        let confirmDialogConfig: ConfirmDialogConfig = {
            dialogType: DialogType.NORMAL,
            title: this.translate.instant(title),
            message: [this.translate.instant(message)],
            confirmButtonText: this.translate.instant(confirmButtonText),
            cancelButtonText: this.translate.instant(cancelButtonText)
        }
        return this.showDialog(confirmDialogConfig);
    }

    /**
     * Add a Show Leave Dialog
     */
    public showLeaveDialog(): Observable<boolean | null | undefined> {
        let confirmDialogConfig: ConfirmDialogConfig = {
            dialogType: DialogType.NORMAL,
            title: this.translate.instant("LABEL_LEAVE_DIALOG_TITLE"),
            message: [this.translate.instant("LABEL_LEAVE_CONFIRMATION")],
            confirmButtonText: this.translate.instant('LABEL_YES'),
            cancelButtonText: this.translate.instant('LABEL_NO')
        }

        return this.showDialog(confirmDialogConfig);
    }
}